import { api } from './api'
import { Mailbox, PaginatedResponse } from '@/types'

/**
 * Mailbox Service
 * Multi-Provider Email Account Management API
 * Version: 2.0.0 (Enhanced with AWS SES, SendGrid, Health Monitoring)
 */

// ============================================================================
// Provider-Specific Configuration Types
// ============================================================================

export interface AwsSesConfig {
  region: string
  accessKeyId: string
  secretAccessKey: string
  configurationSet?: string
}

export interface SmtpConfig {
  host: string
  port: number
  secure: boolean
  auth: {
    user: string
    pass: string
  }
  imap?: {
    host: string
    port: number
    secure: boolean
    auth: {
      user: string
      pass: string
    }
  }
}

export interface SendGridConfig {
  apiKey: string
}

// ============================================================================
// Request/Response Types
// ============================================================================

export interface MailboxFilters {
  status?: 'ACTIVE' | 'INACTIVE' | 'SUSPENDED' | 'ERROR'
  provider?: 'AWS_SES' | 'SMTP' | 'SENDGRID'
  healthStatus?: 'HEALTHY' | 'WARNING' | 'ERROR' | 'UNKNOWN'
  userId?: number
  search?: string
  isActive?: boolean
  availableForRotation?: boolean
  page?: number
  pageSize?: number
}

export interface CreateMailboxRequest {
  name: string
  email: string
  fromName?: string
  replyToEmail?: string
  provider: 'AWS_SES' | 'SMTP' | 'SENDGRID'
  dailyLimit?: number
  notes?: string

  // Provider-specific config (only one should be provided based on provider)
  awsSesConfig?: AwsSesConfig
  smtpConfig?: SmtpConfig
  sendgridConfig?: SendGridConfig
}

export interface UpdateMailboxRequest {
  name?: string
  fromName?: string
  replyToEmail?: string
  provider?: 'AWS_SES' | 'SMTP' | 'SENDGRID'
  dailyLimit?: number
  status?: 'ACTIVE' | 'INACTIVE' | 'SUSPENDED' | 'ERROR'
  notes?: string

  // Provider-specific config updates
  awsSesConfig?: AwsSesConfig
  smtpConfig?: SmtpConfig
  sendgridConfig?: SendGridConfig
}

export interface TestConnectionRequest {
  provider: 'AWS_SES' | 'SMTP' | 'SENDGRID'
  awsSesConfig?: AwsSesConfig
  smtpConfig?: SmtpConfig
  sendgridConfig?: SendGridConfig
}

export interface TestConnectionResult {
  success: boolean
  message: string
  details?: {
    provider: string
    connectionTime: number
    error?: string
  }
}

export interface MailboxStats {
  total: number
  active: number
  inactive: number
  suspended: number
  availableCapacity: number
  todaysSent: number
  byProvider: {
    AWS_SES: number
    SMTP: number
    SENDGRID: number
  }
  byHealthStatus: {
    HEALTHY: number
    WARNING: number
    ERROR: number
    UNKNOWN: number
  }
}

// ============================================================================
// API Service
// ============================================================================

export const mailboxService = {
  async fetchMailboxes(filters: MailboxFilters = {}): Promise<PaginatedResponse<Mailbox>> {
    // Backend returns: { success: true, data: Mailbox[], pagination: { total, page, pageSize } }
    const response = await api.get<{
      success: boolean
      data: Mailbox[]
      pagination: {
        total: number
        page: number
        pageSize: number
      }
    }>('/mailboxes', {
      params: filters,
    })
    return {
      data: response.data.data || [],
      pagination: {
        page: response.data.pagination?.page || 1,
        limit: response.data.pagination?.pageSize || 10,
        total: response.data.pagination?.total || 0,
        totalPages: Math.ceil((response.data.pagination?.total || 0) / (response.data.pagination?.pageSize || 10)),
      },
    }
  },

  async getMailbox(id: number): Promise<Mailbox> {
    // Backend returns: { success: true, data: Mailbox }
    const response = await api.get<{ success: boolean; data: Mailbox }>(`/mailboxes/${id}`)
    return response.data.data
  },

  async createMailbox(data: CreateMailboxRequest): Promise<Mailbox> {
    // Backend returns: { success: true, data: Mailbox }
    const response = await api.post<{ success: boolean; data: Mailbox }>('/mailboxes', data)
    return response.data.data
  },

  async updateMailbox(id: number, data: UpdateMailboxRequest): Promise<Mailbox> {
    // Backend returns: { success: true, data: Mailbox }
    const response = await api.put<{ success: boolean; data: Mailbox }>(`/mailboxes/${id}`, data)
    return response.data.data
  },

  async deleteMailbox(id: number): Promise<void> {
    await api.delete(`/mailboxes/${id}`)
  },

  async testConnection(data: TestConnectionRequest): Promise<TestConnectionResult> {
    const response = await api.post<TestConnectionResult>('/mailboxes/test-connection', data)
    return response.data
  },

  async resetDailyCount(id: number): Promise<void> {
    await api.post(`/mailboxes/${id}/reset-count`)
  },

  async getStats(): Promise<MailboxStats> {
    // Backend may return: { success: true, data: MailboxStats }
    const response = await api.get<{ success: boolean; data: MailboxStats }>('/mailboxes/stats')
    return response.data.data || (response.data as unknown as MailboxStats)
  },
}
